/**
 * Gallop Watch House — Navigation System
 * Desktop mega-menu on NEW / BRANDS (hover) + mobile L1/L2 Brand Explorer
 */

(function () {
  /** Match NEW / New Arrivals / BRAND / BRANDS (and titles containing those). */
  function isMegaTriggerTitle(title) {
    const t = (title || '').replace(/\s+/g, ' ').trim();
    if (!t) return false;
    if (/^(new|brands?)\b/i.test(t)) return true;
    if (/new\s+arrivals?/i.test(t)) return true;
    if (/\bbrands?\b/i.test(t)) return true;
    return false;
  }

  class GwhDesktopBrandsMega {
    constructor(panel) {
      this.panel = panel;
      this.triggerItems = [];
      this.closeTimer = null;
      this.open = false;
      this.homeParent = panel.parentElement;
      this.homeNextSibling = panel.nextSibling;
      this.desktopMq = window.matchMedia('(min-width: 990px)');
      this.init();
    }

    getItemTitle(item) {
      const titleEl = item?.querySelector?.('.menu-list__link-title');
      if (titleEl) {
        return (titleEl.textContent || '').replace(/\s+/g, ' ').trim();
      }
      const link = item?.querySelector?.('.menu-list__link, [ref="menuitem"]');
      return (link?.textContent || '').replace(/\s+/g, ' ').trim();
    }

    isDesktop() {
      return this.desktopMq.matches;
    }

    /**
     * Sticky headers set #header-group { display: contents }, which removes its
     * containing block. Mount the mega under #header-component so absolute
     * positioning tracks the sticky nav bar — desktop only.
     *
     * On mobile the panel must NOT live under #header-component: that node has
     * a navy background, and without desktop absolute positioning the mega
     * height leaks into layout as a large empty navy block.
     */
    mountUnderHeader() {
      if (!this.isDesktop()) {
        this.restoreHome();
        return;
      }

      const header = document.querySelector('#header-component');
      if (!header) return;

      if (this.panel.parentElement !== header) {
        header.appendChild(this.panel);
      }

      header.classList.add('has-gwh-brands-mega');
    }

    restoreHome() {
      const header = document.querySelector('#header-component');
      header?.classList.remove('has-gwh-brands-mega');

      if (!this.homeParent) return;
      if (this.panel.parentElement === this.homeParent) return;

      if (this.homeNextSibling && this.homeNextSibling.parentNode === this.homeParent) {
        this.homeParent.insertBefore(this.panel, this.homeNextSibling);
      } else {
        this.homeParent.appendChild(this.panel);
      }

      this.hide();
    }

    syncViewport() {
      if (this.isDesktop()) {
        this.mountUnderHeader();
        this.refreshTriggers();
      } else {
        this.restoreHome();
      }
    }

    refreshTriggers() {
      document.querySelectorAll('#header-component .menu-list__list-item').forEach((item) => {
        if (item.hasAttribute('slot') && item.getAttribute('slot') === 'more') return;
        const title = this.getItemTitle(item);
        if (!isMegaTriggerTitle(title)) return;

        if (!item.classList.contains('is-brands-trigger')) {
          item.classList.add('is-brands-trigger');
          const link = item.querySelector('.menu-list__link, [ref="menuitem"]');
          if (link) {
            link.setAttribute('aria-haspopup', 'true');
            link.setAttribute('aria-controls', this.panel.id);
            if (!link.hasAttribute('aria-expanded')) {
              link.setAttribute('aria-expanded', 'false');
            }
          }

          // Direct listeners — more reliable than bubbling past Horizon handlers
          item.addEventListener('pointerenter', () => this.show(item));
          item.addEventListener('pointerleave', (event) => {
            const related = event.relatedTarget;
            if (related && (item.contains(related) || this.panel.contains(related))) return;
            this.scheduleHide();
          });
        }

        if (!this.triggerItems.includes(item)) {
          this.triggerItems.push(item);
        }
      });
    }

    init() {
      this.syncViewport();
      this.refreshTriggers();

      const header = document.querySelector('#header-component');
      const headerMenu = document.querySelector('#header-component header-menu');

      const onViewportChange = () => this.syncViewport();
      if (typeof this.desktopMq.addEventListener === 'function') {
        this.desktopMq.addEventListener('change', onViewportChange);
      } else if (typeof this.desktopMq.addListener === 'function') {
        this.desktopMq.addListener(onViewportChange);
      }

      // Horizon fires this after a short dwell on items that have a submenu
      headerMenu?.addEventListener('megaMenu:hover', () => {
        if (!this.isDesktop()) return;
        this.refreshTriggers();
        const active =
          this.triggerItems.find((item) => item.matches(':hover')) ||
          this.triggerItems.find((item) => item.querySelector('[aria-expanded="true"]'));
        if (active) this.show(active);
      });

      // Capture fallback for hydration / overflow slots
      header?.addEventListener(
        'pointerover',
        (event) => {
          if (!this.isDesktop()) return;
          const el = event.target instanceof Element ? event.target : event.target?.parentElement;
          const item = el?.closest?.('.menu-list__list-item');
          if (!item) return;
          if (!item.classList.contains('is-brands-trigger')) {
            if (!isMegaTriggerTitle(this.getItemTitle(item))) return;
            this.refreshTriggers();
          }
          if (!item.classList.contains('is-brands-trigger')) return;
          this.show(item);
        },
        true
      );

      header?.addEventListener(
        'click',
        (event) => {
          if (!this.isDesktop()) return;
          const el = event.target instanceof Element ? event.target : null;
          const link = el?.closest?.(
            '.menu-list__list-item.is-brands-trigger .menu-list__link, .menu-list__list-item.is-brands-trigger a'
          );
          if (!link) return;
          const item = link.closest('.menu-list__list-item');
          event.preventDefault();
          event.stopPropagation();
          this.show(item);
        },
        true
      );

      this.panel.addEventListener('pointerenter', () => this.clearHide());
      this.panel.addEventListener('pointerleave', () => this.scheduleHide());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.open) this.hide();
      });

      document.addEventListener('click', (event) => {
        if (!this.open) return;
        if (this.panel.contains(event.target)) return;
        if (this.triggerItems.some((item) => item.contains(event.target))) return;
        this.hide();
      });

      document.addEventListener('shopify:section:load', () => {
        // Fresh section render may replace the panel node; re-capture home if needed.
        if (this.panel.isConnected && this.panel.parentElement !== document.querySelector('#header-component')) {
          this.homeParent = this.panel.parentElement;
          this.homeNextSibling = this.panel.nextSibling;
        }
        this.syncViewport();
        this.refreshTriggers();
      });

      // Re-scan after Horizon hydrates menu links
      window.setTimeout(() => {
        this.syncViewport();
        this.refreshTriggers();
      }, 400);
      window.setTimeout(() => this.refreshTriggers(), 1200);
    }

    show(item) {
      if (!this.isDesktop()) return;
      this.mountUnderHeader();
      this.clearHide();
      this.open = true;
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('gwh-brands-mega-open');

      this.triggerItems.forEach((trigger) => {
        const active = item ? trigger === item : true;
        trigger.classList.toggle('is-mega-active', active);
        trigger.querySelector('.menu-list__link, [ref="menuitem"]')?.setAttribute(
          'aria-expanded',
          active ? 'true' : 'false'
        );
      });
    }

    hide() {
      this.clearHide();
      this.open = false;
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('gwh-brands-mega-open');
      this.triggerItems.forEach((trigger) => {
        trigger.classList.remove('is-mega-active');
        trigger
          .querySelector('.menu-list__link, [ref="menuitem"]')
          ?.setAttribute('aria-expanded', 'false');
      });
    }

    scheduleHide() {
      this.clearHide();
      this.closeTimer = window.setTimeout(() => this.hide(), 320);
    }

    clearHide() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }
  }

  class GwhMobileNav {
    constructor(root) {
      this.root = root;
      this.overlay = root.querySelector('[data-mobile-menu-overlay]');
      this.closeBtn = root.querySelector('[data-mobile-menu-close]');
      this.backBtn = root.querySelector('[data-mobile-nav-back]');
      this.logo = root.querySelector('[data-mobile-nav-logo]');
      this.levelTitle = root.querySelector('[data-mobile-nav-level-title]');
      this.drillBtns = root.querySelectorAll('[data-mobile-nav-drill]');
      this.isOpen = false;
      this.level = 1;
      this.init();
    }

    init() {
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      this.backBtn?.addEventListener('click', () => this.showLevel(1));

      this.drillBtns.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const item = btn.closest('[data-mobile-nav-item]');
          item?.classList.add('is-active');
          this.showLevel(2);
        });
      });

      this.root.querySelectorAll('[data-mobile-nav-wishlist]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
          window.setTimeout(() => {
            document.querySelector('[data-gwh-wishlist-open]')?.click();
          }, 120);
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          if (this.level === 2) this.showLevel(1);
          else this.close();
        }
      });
    }

    open() {
      this.isOpen = true;
      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.showLevel(1);
      this.syncHeaderTrigger(true);
      this.closeBtn?.focus();
    }

    close() {
      this.isOpen = false;
      this.root.classList.remove('is-open', 'is-level-2');
      this.root.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.level = 1;
      this.root.querySelectorAll('[data-mobile-nav-item].is-active').forEach((el) => {
        el.classList.remove('is-active');
      });
      if (this.logo) this.logo.hidden = false;
      if (this.levelTitle) this.levelTitle.hidden = true;
      if (this.backBtn) this.backBtn.hidden = true;
      this.syncHeaderTrigger(false);
    }

    syncHeaderTrigger(isOpen) {
      const summary = document.querySelector(
        '#Details-menu-drawer-container > summary, .header__icon--menu.header__icon--summary'
      );
      if (!summary) return;
      summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      const details = summary.closest('details');
      if (details) {
        details.classList.toggle('menu-open', isOpen);
        if (!isOpen) {
          details.open = false;
          details.removeAttribute('open');
        }
      }
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    showLevel(level) {
      this.level = level;
      const isL2 = level === 2;
      this.root.classList.toggle('is-level-2', isL2);

      if (this.backBtn) this.backBtn.hidden = !isL2;
      if (this.logo) this.logo.hidden = isL2;
      if (this.levelTitle) this.levelTitle.hidden = !isL2;

      if (!isL2) {
        this.root.querySelectorAll('[data-mobile-nav-item].is-active').forEach((el) => {
          el.classList.remove('is-active');
        });
      }
    }
  }

  function bridgeHamburger(mobileNav) {
    if (!mobileNav) return;

    document.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest?.(
          '#Details-menu-drawer-container > summary, .header__icon--menu.header__icon--summary'
        );
        if (!summary) return;
        if (!window.matchMedia('(max-width: 989px)').matches) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const details = summary.closest('details');
        if (details) {
          details.open = false;
          details.removeAttribute('open');
        }

        mobileNav.toggle();
        /* open()/close() sync .menu-open + aria-expanded on the header trigger */
      },
      true
    );
  }

  function initMega() {
    const mega = document.querySelector('[data-gwh-brands-mega]');
    if (!mega || mega.dataset.gwhMegaInit === '1') return;
    // Always bind — handlers themselves gate on desktop breakpoint
    mega.dataset.gwhMegaInit = '1';
    new GwhDesktopBrandsMega(mega);
  }

  function init() {
    initMega();

    const mobileRoot = document.querySelector('[data-mobile-menu].gwh-mobile-nav');
    if (mobileRoot && !mobileRoot.dataset.gwhMobileInit) {
      mobileRoot.dataset.gwhMobileInit = '1';
      const mobileNav = new GwhMobileNav(mobileRoot);
      window.GWHMobileMenu = {
        open: () => mobileNav.open(),
        close: () => mobileNav.close(),
        toggle: () => mobileNav.toggle(),
      };
      bridgeHamburger(mobileNav);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
